import { useState, useEffect } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useLocation, Outlet } from 'react-router'
import { toast } from 'sonner'
import { PersonLockRegular, SpinnerIosRegular } from '@fluentui/react-icons'
import { useSessionStorage, useLogout, useSingleTabSession, requestSessionTakeover } from '@/hooks'
import { isTokenExpired } from '@/hooks/useTokenValidator'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Button } from '@/components/shadcn/button'
import { LoginPage } from '@/ux/LoginPage'
import { PWC_ID_CONFIG } from '@/const'
import { hasLeftTabIntent } from '@/utils/tabSession'

export const ProtectedRoutes = () => {
  const [token] = useSessionStorage('token', '')
  const location = useLocation()
  const { pathname, search } = location

  const redirectPath = JSON.stringify({
    pathname,
    search: { ...Object.fromEntries(new URLSearchParams(search)), authStatus: 'login-success' },
  })

  if (!token || hasLeftTabIntent() || isTokenExpired(token)) return <LoginPage redirectPath={redirectPath} />

  return (
    <SingleTabSessionGuard redirectPath={redirectPath}>
      <AutoLogout>
        <Outlet />
      </AutoLogout>
    </SingleTabSessionGuard>
  )
}

const SingleTabSessionGuard = ({ children, redirectPath }: { children: React.ReactNode; redirectPath: string }) => {
  const { dismissLocalSession } = useLogout()
  const { isSessionHolder, isChecking, isSuperseded } = useSingleTabSession(true)

  const handleTakeOver = async () => {
    await requestSessionTakeover()
    const state = JSON.stringify({
      ...JSON.parse(redirectPath),
      forceNewSession: true,
    })
    window.location.replace(PWC_ID_CONFIG.authUrl(state))
  }

  if (isChecking) {
    return (
      <SessionAreaMessage
        title="Checking your session"
        description="Please wait while we verify this browser tab can use your login."
      >
        <SpinnerIosRegular fontSize={24} className="animate-spin text-foreground/50" />
      </SessionAreaMessage>
    )
  }

  if (!isSessionHolder) {
    if (isSuperseded) {
      return (
        <SessionAreaMessage
          title="Your session is open in another tab"
          description="You signed in from a different tab in this browser. Continue working there, or go to the home page in this tab."
        >
          <Button variant="outline" onClick={dismissLocalSession}>
            Go to home
          </Button>
        </SessionAreaMessage>
      )
    }

    return (
      <SessionAreaMessage
        title="This tab cannot use your session"
        description="Another browser tab already has an active login (for example a duplicated tab). Use this tab to sign in again and close other sessions, or close this tab to continue in the other one."
      >
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={handleTakeOver}>Use this tab</Button>
          <Button variant="outline" onClick={dismissLocalSession}>
            Close this tab
          </Button>
        </div>
      </SessionAreaMessage>
    )
  }

  return <>{children}</>
}

const SessionAreaMessage = ({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children?: React.ReactNode
}) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-y-6 px-6 py-12">
    <PersonLockRegular fontSize={72} className="text-foreground/50" />
    <div className="flex max-w-lg flex-col items-center gap-y-2 text-center">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="text-sm text-foreground/70">{description}</p>
    </div>
    {children}
  </div>
)

const AutoLogout = ({ children }: { children: React.ReactNode }) => {
  const timeout = 1000 * 60 * 15 // 15 minutes
  const promptBeforeIdle = 1000 * 60 * 0.5 // 30 seconds

  const [remaining, setRemaining] = useState<number>(timeout)
  const { logoutHandler } = useLogout()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const onPrompt = () => {
    setIsDialogOpen(true)
  }

  const onIdle = () => {
    toast.dismiss('prompt/active') // Clear the warning toast if it's still showing
    logoutHandler()
  }

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  })

  const handleStillActive = () => {
    setIsDialogOpen(false)
    activate()
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000))
    }, 500)

    return () => {
      clearInterval(interval)
    }
  })

  return (
    <>
      {children}
      <ConfirmDialog
        title="Are you still here?"
        description={`Logging out in ${remaining} seconds`}
        cancelLabel="Logout"
        confirmLabel="I'm still here"
        onCancel={logoutHandler}
        onConfirm={handleStillActive}
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </>
  )
}
